<template>
  <div class="order-menu-v-four">
    <p class="order-menu-v-four__title">
      Меню
    </p>
    <Swiper
      :space-between="0"
      :slides-per-view="slidesPerView"
      centered-slides
      centered-slides-bounds
      class="order-menu-v-four__days-swiper"
      @swiper="setDaySwiper"
    >
      <SwiperSlide
        v-for="day in filteredMenuDays"
        :key="`menu-day-${day.date}`"
      >
        <CoreButton
          :type="getButtonType(day.index)"
          w100
          class="order-menu-v-four__btn"
          :class="getButtonClasses(day.index)"
          @click="selectDay(day.index)"
        >
          <div
            class="order-menu-v-four__week-day"
            :class="getWeekDayClasses(day.index)"
          >
            {{ day.dayOfWeek }}
          </div>
          <div
            class="order-menu-v-four__date"
            :class="getDateClasses(day.index)"
          >
            {{ day.dayNumber }} {{ day.month }}
          </div>
        </CoreButton>
      </SwiperSlide>
    </Swiper>
    <div
      v-if="showPackCalories"
      class="order-menu-v-four__nutrients"
      :class="nutrientsClasses"
    >
      <p class="order-menu-v-four__nutrient-count">
        {{ nutrients.proteins }}<span class="order-menu-v-four__nutrient-type"> Б</span>
      </p>
      <p class="order-menu-v-four__nutrient-count">
        {{ nutrients.fats }}<span class="order-menu-v-four__nutrient-type"> Ж</span>
      </p>
      <p class="order-menu-v-four__nutrient-count">
        {{ nutrients.carbs }}<span class="order-menu-v-four__nutrient-type"> У</span>
      </p>
      <p class="order-menu-v-four__nutrient-count">
        {{ nutrients.calories }}<span class="order-menu-v-four__nutrient-type"> Ккал</span>
      </p>
    </div>
    <Swiper
      :space-between="8"
      class="order-menu-v-four__packs-swiper"
      @swiper="setSwiper"
      @slideChange="onSlideChange"
    >
      <SwiperSlide
        v-for="(dayPacks, index) in filteredAllPacks"
        :key="`dayPacks-${index}`"
      >
        <div
          v-if="index !== allPacks.length - 1"
          class="order-menu-v-four__packs"
        >
          <MenuPack
            v-for="pack in dayPacks"
            :key="pack.id"
            :pack="pack"
            :show-calories="showPackCalories"
          />
          <EmptyPack v-if="isEmptyPackDisplayed" />
        </div>
        <div
          v-else
          class="order-menu-v-four__last-day"
          :class="lastDayClasses"
          :style="lastDayContainerStyles"
        >
          <img
            src="@/assets/images/newOrder/empty-pack.svg"
            alt=""
            height="99"
            class="order-menu-v-four__last-day-img"
          >
          <div>
            <p class="order-menu-v-four__last-day-title">
              Четыре недели<br v-if="hasSmallSize"> без повторов блюд
            </p>
            <p class="order-menu-v-four__last-day-description">
              Меню на эту и следующие даты будет сформировано позже
            </p>
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script>
import CoreButton from '@/components/core/ui/CoreButton'
import MenuPack from '@/components/newOrderV4/OrderMenu/MenuPack'
import EmptyPack from '@/components/newOrderV4/OrderMenu/EmptyPack'
import { Swiper, SwiperSlide } from 'swiper/swiper-vue'

export default {
  name: 'OrderMenu',

  components: {
    CoreButton,
    MenuPack,
    EmptyPack,
    Swiper,
    SwiperSlide,
  },

  props: {
    menu: {
      type: Object,
      required: true,
    },
    showPackCalories: {
      type: Boolean,
      default: false,
    },
    selectedMenuType: {
      type: [String, null],
      default: null,
    },
  },

  data () {
    return {
      selectedDayIndex: 0,
      lastDayContainerHeight: 0,
      swiper: null,
      daysSwiper: null,
    }
  },

  computed: {
    isLastDaySelected () {
      return this.selectedDayIndex === this.menuDays.length - 1
    },

    lastDayContainerStyles () {
      return {
        height: this.lastDayContainerHeight ? `${this.lastDayContainerHeight}px` : 'auto',
      }
    },

    hasSmallSize () {
      return this.packs.length < 3
    },

    lastDayClasses () {
      return {
        'order-menu-v-four__last-day--small': this.hasSmallSize,
      }
    },

    nutrientsClasses () {
      return {
        'order-menu-v-four__nutrients--hidden': this.isLastDaySelected,
      }
    },

    nutrients () {
      const nutrients = {
        calories: 0,
        proteins: 0,
        fats: 0,
        carbs: 0,
      }

      this.packs.reduce((prevValue, currValue) => {
        const energyData = currValue.energyData
        prevValue.calories += energyData.kcal
        prevValue.proteins += energyData.protein
        prevValue.carbs += energyData.carbs
        prevValue.fats += energyData.fat
        return prevValue
      }, nutrients)

      return nutrients
    },

    menuDays () {
      const days = []
      this.menu.days.forEach((day, index) => {
        const date = new Date(day.date)
        const dayOfWeek = date.toLocaleDateString('ru-RU', { weekday: 'short' })
        const month = date.toLocaleDateString('ru-RU', { month: 'short' }).substring(0, 3)
        const dayNumber = date.getDate()
        days.push({
          dayOfWeek,
          dayNumber,
          month,
          date: day.date,
          items: day.meals,
          index,
        })
      })
      return days
    },

    customization () {
      return this.menu?.customization || []
    },

    allPacks () {
      const allPacks = []
      this.menuDays.forEach((menuDay) => {
        const dayPacks = []
        menuDay.items.forEach((item) => {
          if (item.packs?.length) {
            dayPacks.push({
              ...item.packs[0],
              mealTitle: item.title,
            })
          }
        })
        allPacks.push(dayPacks)
      })
      return allPacks
    },

    packs () {
      return this.allPacks[this.selectedDayIndex]
    },

    isEmptyPackDisplayed () {
      return this.packs.length % 2
    },

    isDetoxSelected () {
      return this.selectedMenuType === 'detox'
    },

    slidesPerView () {
      return this.isDetoxSelected ? 2 : 6
    },

    filteredMenuDays () {
      return this.filterItems(this.menuDays)
    },

    filteredAllPacks () {
      return this.filterItems(this.allPacks)
    },
  },

  mounted () {
    const packsBlock = document.querySelector('.order-menu-v-four__packs')
    this.lastDayContainerHeight = packsBlock.clientHeight
    this.daysSwiper.setProgress(0, 0)
  },

  methods: {
    setSwiper (swiper) {
      this.swiper = swiper
    },

    setDaySwiper (swiper) {
      this.daysSwiper = swiper
    },

    onSlideChange () {
      this.daysSwiper.slideTo(this.swiper.activeIndex)
      this.selectDay(this.swiper.activeIndex)
    },

    isSelectedDay (dayIndex) {
      return dayIndex === this.selectedDayIndex
    },

    getButtonType (dayIndex) {
      if (this.isSelectedDay(dayIndex)) {
        return 'from-var'
      }
      return 'text-grey'
    },

    selectDay (dayIndex) {
      this.swiper.slideTo(dayIndex)
      this.selectedDayIndex = dayIndex

      this.sendGTMMarkupEvent({
        block: 'new_order',
        element: 'menu_date',
        action: 'change',
      })

      if (this.isLastDaySelected) {
        this.sendGTMMarkupEvent({
          block: 'new_order',
          element: 'empty_menu',
          action: 'show',
        })
      }
    },

    getButtonClasses (dayIndex) {
      return {
        'order-menu-v-four__btn--has-line': !this.isSelectedDay(dayIndex) && dayIndex !== this.selectedDayIndex - 1 && dayIndex !== this.menuDays.length - 1 && !this.isDetoxSelected,
      }
    },

    getWeekDayClasses (dayIndex) {
      return {
        'order-menu-v-four__week-day--inactive': !this.isSelectedDay(dayIndex),
      }
    },

    getDateClasses (dayIndex) {
      return {
        'order-menu-v-four__date--inactive': !this.isSelectedDay(dayIndex),
      }
    },

    filterItems (items) {
      return this.isDetoxSelected ? items.slice(0, 2) : items
    },
  },
}
</script>

<style lang="scss">
.swiper.order-menu-v-four__days-swiper {
  margin: 0 -16px 12px;
  padding: 0 16px;

  & .swiper-slide {
    box-shadow: inset 0 1px 0 0 map-get($colors, 'gray_8'), inset 0 -1px 0 0 map-get($colors, 'gray_8');

    &:first-child {
      border-radius: 16px 0 0 16px;
      box-shadow: inset 1px 0 0 0 map-get($colors, 'gray_8'), inset 0 1px 0 0 map-get($colors, 'gray_8'), inset 0 -1px 0 0 map-get($colors, 'gray_8')
    }

    &:last-child {
      border-radius: 0 16px 16px 0;
      box-shadow: inset -1px 0 0 0 map-get($colors, 'gray_8'), inset 0 1px 0 0 map-get($colors, 'gray_8'), inset 0 -1px 0 0 map-get($colors, 'gray_8')
    }
  }
}

.order-menu-v-four {
  $this: &;

  margin-top: 32px;
  padding: 0 16px;

  &__title {
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    padding-bottom: 16px;
  }

  &__days-swiper {
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    height: 48px;
    margin-bottom: 12px;
    overflow: hidden;
    position: relative;
  }

  &__btn {
    min-height: 48px;
    border-radius: 16px;
    padding: 0;
    text-transform: none;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 3px;
    z-index: 1;

    &--has-line::after {
      content: '';
      position: absolute;
      top: 14px;
      bottom: 14px;
      right: 0;
      border: 1px solid map-get($colors, 'gray_8');
    }
  }

  &__week-day {
    font-size: 14px;
    line-height: 16px;
    text-transform: capitalize;
    font-weight: 400;
    letter-spacing: -0.15px;

    &--inactive {
      color: map-get($colors, 'grey_80');
    }
  }

  &__date {
    font-size: 8px;
    font-weight: 600;
    line-height: 8px;
    text-transform: uppercase;
    letter-spacing: 0.6px;

    &--inactive {
      color: map-get($colors, 'grey_40');
    }
  }

  &__packs {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  &__nutrients {
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
    line-height: 28px;
    color: map-get($colors, 'black');

    &--hidden {
      visibility: hidden;
    }
  }

  &__nutrient-count {
    margin-right: 12px;

    &:last-child {
      margin-right: 0;
      margin-left: 12px;
    }
  }

  &__nutrient-type {
    color: map-get($colors, 'secondary');
  }

  &__last-day {
    border-radius: 16px;
    background: map-get($colors, 'grey_4');
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 23px;

    &--small {
      flex-direction: row;
      gap: 20px;
      padding: 23px;

      & {
        #{$this}__last-day-img {
          height: 99px;
          width: 148px;
          margin-bottom: 0;
        }

        #{$this}__last-day-title {
          font-size: 16px;
          line-height: normal;
          text-align: left;
          margin-bottom: 12px;
          max-width: 159px;
        }

        #{$this}__last-day-description {
          font-size: 12px;
          line-height: normal;
          text-align: left;
          max-width: 143px;
        }
      }
    }
  }

  &__last-day-img {
    height: 163px;
    margin-bottom: 29px;
  }

  &__last-day-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
    max-width: 268px;
    text-align: center;
    margin-bottom: 20px;
  }

  &__last-day-description {
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    max-width: 282px;
  }
}
</style>
